import config from '@/helpers/config'

export default {
  debounce(fn, wait) {
    let timer

    return function(...args) {
      if (timer) {
        clearTimeout(timer)
      }

      const context = this
      timer = setTimeout(()=>{
        fn.apply(context, args)
      }, wait)
    }
  },

  highlight (content, keywords) {
    let words = keywords
    let text = content

    if (words.length > 0) {
      if (Array.isArray(keywords)) {
        let regexStr = words.join('|')
        text = text.replace(new RegExp(`\\b${regexStr}\\b`, 'gi'), function (word) {
          return '<span class="highlight--search">' + word + '</span>'
        })
      } else {
        words = encodeURIComponent(words)
        text = text.replace(new RegExp(`${words}`, 'gi'), function (word) {
          return '<span class="highlight--search">' + word + '</span>'
        })
      }
    }

    return text
  },

  // Expected datestamp format = 2024-03-27 12:11:36
  formatDate (datestamp) {
    let formattedDate = '-'

    if (datestamp.split(' ')) {
      let splitStamp = datestamp.split(' ')

      if (splitStamp[0].split('-')) {
        let splitDate = splitStamp[0].split('-')

        if (splitDate.length === 3) {
          let years = splitDate[0]
          let months = splitDate[1]
          let days = splitDate[2]
          let fullMonths = {
            '01': 'January',
            '02': 'February',
            '03': 'March',
            '04': 'April',
            '05': 'May',
            '06': 'June',
            '07': 'July',
            '08': 'August',
            '09': 'September',
            '10': 'October',
            '11': 'November',
            '12': 'December'
          }

          if (days < 10) {
            days = days.slice(1)
          }

          days = parseInt(days, 10)

          if (days === 1 || days === 21 || days === 31) {
            days = days + 'st'
          } else if (days === 2 || days === 22) {
            days = days + 'nd'
          } else if (days === 3 || days === 23) {
            days = days + 'rd'
          } else {
            days = days + 'th'
          }

          formattedDate = days + ' of ' + fullMonths[months] + ' ' + years
        }
      }
    }

    return formattedDate
  },

  formatNumber (number, decimals) {
    let string, prefix, suffix = ''
    let fixedDecimals = decimals || 0
    let factor = Math.pow(10, fixedDecimals)
    number = +number

    if (number < 0) {
      number = -1 * number
      prefix = '-'
    }

    if (number < 1000) {
      if (decimals === 1) {
        string = Math.floor(number / (1 / factor)) / factor
      } else {
        string = number
      }
    } else if (number < 1000000) {
      string = Math.floor(number / (1000 / factor)) / factor
      suffix = 'k'
    } else if (number < 1000000000) {
      string = Math.floor(number / (1000000 / factor)) / factor
      suffix = 'M'
    } else if (number < 1000000000000) {
      string = Math.floor(number / (1000000000 / factor)) / factor
      suffix = 'B'
    } else if (number < 1000000000000000) {
      string = Math.floor(number / (1000000000000 / factor)) / factor
      suffix = 'T'
    } else if (number < 1000000000000000000) {
      string = Math.floor(number / (1000000000000000 / factor)) / factor
      suffix = 'q'
    } else {
      string = Math.floor(number / (1000000000000000000 / factor)) / factor
      suffix = 'Q'
    }

    if (prefix) {
      return prefix + string + suffix
    }

    return string + suffix
  },

  separateThousands (value) {
    let reversed = [...String(value)].reverse().join('')
    let separated = reversed.replace(/(.{3})/g, '$1 ')
    let finalized = [...separated].reverse().join('')

    if (finalized.charAt(0) === ' ') {
      return finalized.substring(1)
    }

    return finalized
  },

  getDefaultChart (dataSeries) {
    let type = 'bar'
    let fullType = 'ver_bar'
    let stack = false
    let axis = 'yAxis'
    let seriesAmount = Object.keys(dataSeries).length
    let maxValueAmount = 1
    let maxLabelLength = 1

    for (const [key, value] of Object.entries(dataSeries)) {
      maxValueAmount = maxValueAmount < Object.keys(value).length ? Object.keys(value).length : maxValueAmount

      for (const [innerKey, innerValue] of Object.entries(dataSeries[key])) {
        let check = innerKey.trim()
        maxLabelLength = maxLabelLength < check.length ? check.length : maxLabelLength
      }
    }

    if (seriesAmount === 1) {
      type = 'bar'
      fullType = 'ver_bar'
      stack = false

      if (maxValueAmount > 9) {
        type = 'line'
        fullType = 'hor_line'
        stack = false
        axis = 'yAxis'
      }
    } else if (seriesAmount > 1) {
      /*type = 'pie'
      fullType = 'pie'
      stack = false*/
      type = 'bar'
      fullType = 'ver_bar'
      stack = false

      if (maxValueAmount > 1) {
        type = 'bar'
        fullType = 'ver_bar'
        stack = 'total'
      }
    }

    if (maxLabelLength > 7) {
      type = 'bar'
      fullType = 'hor_bar'
      //stack = 'total'
      axis = 'xAxis'
    }

    return { type: type, fullType: fullType, stack: stack, axis: axis }
  },

  getSearchView () {
    let view = 'search'
    let views = ['datasets', 'pdfs', 'news']

    if (views.includes(this.$route.name.toLowerCase())) {
      view = this.$route.name.toLowerCase()
    }

    return view
  },

  getTypeName (key) {
    if (config.typeNames[key]) {
      return config.typeNames[key]
    }

    return ''
  },

  getFilterName (key) {
    if (config.filterNames[key]) {
      return config.filterNames[key]
    }

    return ''
  },

  getFilterIcon (key) {
    if (config.icons[key]) {
      return config.icons[key].icon
    }

    return ''
  },

  getFilterIconSize (key) {
    if (config.icons[key]) {
      return config.icons[key].iconSize
    }

    return ''
  },

  getConditionName (key) {
    for (const condition of config.filterConditions) {
      if (condition.key === key) {
        return condition.name
      }
    }

    return ''
  },

  getConditionSymbol (key) {
    for (const condition of config.filterConditions) {
      if (condition.key === key) {
        return condition.symbol
      }
    }

    return ''
  },

  itemFound (obj, list) {
    let i
    for (i in list) {
      if (list[i].id === obj.id && list[i].type === obj.type) {
        if (obj.type === 'published') {
          if (list[i].attributes.condition === obj.attributes.condition) {
            return i
          }
        } else {
          return i
        }
      }
    }

    return false
  },

  capitalizeText (str) {
    let splitStr = str.split(' ')

    for (var i = 0; i < splitStr.length; i++) {
      splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
    }

    return splitStr.join(' ')
  },

  links (text, linkClass = 'link--blue') {
    let textArr = []

    text.split(' ').forEach(function (word) {
      let formattedWord = ''
      let parenthesisA = false
      let parenthesisB = false

      if (word.slice(0, 1) === '(') {
        parenthesisA = true
        word = word.slice(1)
      }

      if (word.slice(-1) === ')') {
        parenthesisB = true
        word = word.slice(0, -1)
      }

      if (word.startsWith('https://') || word.startsWith('http://')) {
        if (parenthesisA === true || parenthesisB === true) {
          if (parenthesisA === true) {
            formattedWord = '(<a class="' + linkClass + '" target="_blank" href="' + word + '">' + word + '</a>'
          }

          if (parenthesisB === true) {
            formattedWord = formattedWord + ')'
          }
        } else if (word.slice(-1) === ',') {
          word = word.slice(0, -1)
          formattedWord = '<a class="' + linkClass + '" target="_blank" href="' + word + '">' + word + '</a>,'
        } else {
          formattedWord = '<a class="' + linkClass + '" target="_blank" href="' + word + '">' + word + '</a>'
        }

        textArr.push(formattedWord)
      } else {
        if (parenthesisA === true) {
          word = '(' + word
        }
        if (parenthesisB === true) {
          word = word + ')'
        }

        textArr.push(word)
      }
    })

    return textArr.join(' ')
  },

  createRipple(event) {
    let element = event.currentTarget
    let circle = document.createElement('span')
    let diameter = Math.max(element.clientWidth, element.clientHeight)
    let radius = diameter / 2

    circle.style.width = circle.style.height = `${diameter}px`
    circle.style.left = `-${(diameter / 2) - (element.clientWidth / 2)}px` //`${event.clientX - (element.offsetLeft + radius)}px`
    circle.style.top = `-${(diameter / 2) - (element.clientHeight / 2)}px` // `${event.clientY - (element.offsetTop + radius)}px` // 
    circle.classList.add('ripple')

    let ripple = element.getElementsByClassName('ripple')[0]
    if (ripple) {
      ripple.remove()
    }

    element.appendChild(circle)
  },

  /*getSubscriptionStatus (licenses) {
    let status = 'guest'

    if (licenses.length > 0) {
      for (let license of licenses) {
        if (license.status === 'valid' && license.trial === true) {
          status = 'trial'
        } else if (license.status === 'valid' && license.trial === false) {
          status = 'member'
          break
        }
      }
    }

    return status
  },*/

  sendGoogleAnalyticsEvent (name, eventValue, additionalData, subscriptionStatus) {
    if (process.env.VUE_APP_URL === 'https://app.statzon.com/') {
      gtag('event', name, {
        event_value: eventValue,
        additional_data: additionalData,
        subscription_status: subscriptionStatus
        // TODO: Add type later (added to GA4 already)
      })
    } else {
      console.log({
        GA4_event: {
          name: name,
          event_value: eventValue,
          additional_data: additionalData,
          subscription_status: subscriptionStatus
        }
      })
    }

    return true
  },

  generateSearchToken (url) {
    let hash = 0

    for (let i=0, len=url.length; i<len; i++) {
      let chr = url.charCodeAt(i)
      hash = (hash << 5) - hash + chr
      hash |= 0
    }

    return '_' + hash
  },

  getCookie (cookieName) {
    let cookie = {}

    document.cookie.split(';').forEach(function(el) {
      let [key,value] = el.split('=')
      cookie[key.trim()] = value
    })

    if (cookie[cookieName]) {
      return cookie[cookieName]
    }

    return false
  },

  getHostname (url) {
    let fullUrl = new URL(url)
    let hostname = fullUrl.hostname

    if (hostname.length > 0) {
      if (hostname.substring(0,4) === 'www.') {
        return hostname.substring(4)
      }

      return hostname
    }

    return false
  }
}
